import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, SmartDataTable, getStatusBadge, Modal } from '@/backoffice/components/ui';
// import { formatRelativeTime } from '@/backoffice/utils';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import { UserPermissionsEditor } from '@/backoffice/components/admin/UserPermissionsEditor';
import {
  fetchAllUsers,
  fetchUserDetail,
  updateUserStatus,
  updateUser,
  deactivateUser,
  activateUser,
  fetchRelayPoints,
  fetchAccountTypes,
  getPrimaryTypeName,
  isStaffUser,
  isSuperAdminUser,
  type AdminUser,
} from '@/domains/admin/services/admin.service';
import { Ban, UserCheck, UserX, Power, Building2 } from 'lucide-react';

type FilterType = 'all' | 'client' | 'pro_driver' | 'community_driver' | 'relay_point' | 'staff';

function mapRole(user: AdminUser): string {
  const names = user.types?.map((t) => t.name) ?? [];
  if (names.includes('relay_point')) return 'relay_point';
  const name = getPrimaryTypeName(user);
  if (name === 'professional_delivery') return 'pro_driver';
  if (name === 'community_delivery') return 'community_driver';
  return name;
}

export function AdminUsersPage() {
  const userType = useAdminContext();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [relayPoints, setRelayPoints] = useState<Array<{ id: number; name: string }>>([]);
  const [relayPointTypeId, setRelayPointTypeId] = useState<number | null>(null);
  const [selectedRelayPointId, setSelectedRelayPointId] = useState<number | ''>('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await fetchAllUsers());
    } catch {
      setError('Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    fetchRelayPoints()
      .then((points) => setRelayPoints(points.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => setRelayPoints([]));
    fetchAccountTypes()
      .then((types) => setRelayPointTypeId(types.find((t) => t.name === 'relay_point')?.id ?? null))
      .catch(() => setRelayPointTypeId(null));
  }, [loadUsers]);

  const filteredUsers = activeFilter === 'all'
    ? users
    : activeFilter === 'staff'
      ? users.filter(isStaffUser)
      : users.filter((u) => mapRole(u) === activeFilter);

  const handleView = async (user: AdminUser) => {
    try {
      const detail = await fetchUserDetail(user.id);
      setSelectedUser(detail);
      setSelectedRelayPointId(detail.relay_point_id ?? detail.relay_point?.id ?? '');
      setShowViewModal(true);
    } catch {
      setSelectedUser(user);
      setSelectedRelayPointId(user.relay_point_id ?? user.relay_point?.id ?? '');
      setShowViewModal(true);
    }
  };

  const handleAssignRelayPoint = async () => {
    if (!selectedUser || !relayPointTypeId || !selectedRelayPointId) return;
    setActionLoading(true);
    try {
      const typeIds = Array.from(new Set([
        ...(selectedUser.types?.map((t) => t.id) ?? []),
        relayPointTypeId,
      ]));
      const updated = await updateUser(selectedUser.id, {
        firstnames: selectedUser.firstnames,
        lastname: selectedUser.lastname,
        phone: selectedUser.phone,
        email: selectedUser.email,
        status: selectedUser.status,
        zone_id: selectedUser.zone?.id ?? 1,
        secteur_id: selectedUser.secteur?.id ?? null,
        relay_point_id: Number(selectedRelayPointId),
        type_ids: typeIds,
      });
      setSelectedUser(updated);
      await loadUsers();
    } catch {
      setError('Échec de l\'assignation au point relais.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (user: AdminUser) => {
    if (isSuperAdminUser(user)) {
      setError('Un super-administrateur ne peut pas être suspendu.');
      return;
    }
    setActionLoading(true);
    try {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      await updateUserStatus(user.id, newStatus);
      await loadUsers();
      if (selectedUser?.id === user.id) {
        setSelectedUser(await fetchUserDetail(user.id));
      }
    } catch {
      setError('Échec de la mise à jour du statut.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (user: AdminUser) => {
    if (isSuperAdminUser(user)) {
      setError('Un super-administrateur ne peut pas être désactivé.');
      return;
    }
    setActionLoading(true);
    try {
      await deactivateUser(user.id);
      await loadUsers();
      if (selectedUser?.id === user.id) {
        setSelectedUser(await fetchUserDetail(user.id));
      }
    } catch {
      setError('Échec de la désactivation du compte.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async (user: AdminUser) => {
    setActionLoading(true);
    try {
      await activateUser(user.id);
      await loadUsers();
      if (selectedUser?.id === user.id) {
        setSelectedUser(await fetchUserDetail(user.id));
      }
    } catch {
      setError('Échec de l\'activation du compte.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <DashboardLayout userType={userType} title="Utilisateurs">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 mb-6">
        {[
          { value: 'all', label: 'Tous' },
          { value: 'client', label: 'Clients' },
          { value: 'pro_driver', label: 'Livreurs Pro' },
          { value: 'community_driver', label: 'Livreurs Comm.' },
          { value: 'relay_point', label: 'Points relais' },
          { value: 'staff', label: 'Admins' },
        ].map((filter) => (
          <Button
            key={filter.value}
            variant={activeFilter === filter.value ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter(filter.value as FilterType)}
          >
            {filter.label}
            <Badge variant={activeFilter === filter.value ? 'primary' : 'default'} size="sm" className="ml-2">
              {filter.value === 'all'
                ? users.length
                : filter.value === 'staff'
                  ? users.filter(isStaffUser).length
                  : users.filter((u) => mapRole(u) === filter.value).length}
            </Badge>
          </Button>
        ))}
      </div>

      <Card>
        {loading ? (
          <p className="p-8 text-center text-gray-500">Chargement...</p>
        ) : (
          <SmartDataTable
            data={filteredUsers}
            columns={[
              {
                key: 'firstnames',
                title: 'Utilisateur',
                sortable: true,
                render: (item) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {item.firstnames[0]}{item.lastname[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.firstnames} {item.lastname}</p>
                      <p className="text-xs text-gray-500">{item.phone}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: 'types',
                title: 'Type',
                render: (item) => {
                  const role = mapRole(item);
                  const roleConfig: Record<string, { variant: 'default' | 'primary' | 'success'; label: string }> = {
                    client: { variant: 'default', label: 'Client' },
                    pro_driver: { variant: 'primary', label: 'Livreur Pro' },
                    community_driver: { variant: 'success', label: 'Livreur Comm.' },
                    relay_point: { variant: 'default', label: 'Point relais' },
                    admin: { variant: 'primary', label: 'Admin' },
                    super_admin: { variant: 'primary', label: 'Super Admin' },
                  };
                  const config = roleConfig[role] ?? { variant: 'default' as const, label: role };
                  return <Badge variant={config.variant}>{config.label}</Badge>;
                },
              },
              {
                key: 'status',
                title: 'Statut',
                sortable: true,
                render: (item) => getStatusBadge(item.status),
              },
              {
                key: 'zone',
                title: 'Zone',
                render: (item) => item.zone?.name ?? '—',
              },
            ]}
            keyExtractor={(item) => String(item.id)}
            onView={handleView}
            emptyMessage="Aucun utilisateur"
          />
        )}
      </Card>

      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Détails utilisateur" size="lg">
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-bold text-xl">
                  {selectedUser.firstnames[0]}{selectedUser.lastname[0]}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">
                  {selectedUser.firstnames} {selectedUser.lastname}
                </h4>
                <p className="text-gray-600">{selectedUser.phone}</p>
                <p className="text-sm text-gray-500">{selectedUser.email || 'Pas d\'email'}</p>
              </div>
              <div className="ml-auto flex gap-2">
                {selectedUser.status === 'active' ? (
                  <>
                    {!isSuperAdminUser(selectedUser) && (
                      <>
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<Ban className="w-4 h-4" />}
                          isLoading={actionLoading}
                          onClick={() => handleToggleStatus(selectedUser)}
                        >
                          Suspendre
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<UserX className="w-4 h-4" />}
                          isLoading={actionLoading}
                          onClick={() => handleDeactivate(selectedUser)}
                        >
                          Désactiver
                        </Button>
                      </>
                    )}
                    {isSuperAdminUser(selectedUser) && (
                      <Badge variant="secondary">Compte protégé</Badge>
                    )}
                  </>
                ) : selectedUser.status === 'inactive' ? (
                  <Button
                    variant="success"
                    size="sm"
                    leftIcon={<Power className="w-4 h-4" />}
                    isLoading={actionLoading}
                    onClick={() => handleActivate(selectedUser)}
                  >
                    Activer
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    size="sm"
                    leftIcon={<UserCheck className="w-4 h-4" />}
                    isLoading={actionLoading}
                    onClick={() => handleToggleStatus(selectedUser)}
                  >
                    Réactiver
                  </Button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Type de compte</p>
                <p className="font-medium text-gray-900 capitalize">{mapRole(selectedUser).replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Statut</p>
                {getStatusBadge(selectedUser.status)}
              </div>
              <div>
                <p className="text-sm text-gray-500">Zone</p>
                <p className="font-medium text-gray-900">{selectedUser.zone?.name ?? '—'}</p>
              </div>
              {mapRole(selectedUser) === 'relay_point' && (
                <div>
                  <p className="text-sm text-gray-500">Point relais</p>
                  <p className="font-medium text-gray-900">{selectedUser.relay_point?.name ?? '—'}</p>
                </div>
              )}
            </div>

            <Card className="p-4 bg-violet-50 border-violet-100">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-violet-600" />
                <h4 className="font-medium text-gray-900">Personnel point relais</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Assignez un compte à un point relais pour lui donner accès au tableau de bord dédié.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={selectedRelayPointId}
                  onChange={(e) => setSelectedRelayPointId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Choisir un point relais</option>
                  {relayPoints.map((point) => (
                    <option key={point.id} value={point.id}>{point.name}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  isLoading={actionLoading}
                  disabled={!selectedRelayPointId || !relayPointTypeId}
                  onClick={handleAssignRelayPoint}
                >
                  Assigner
                </Button>
              </div>
            </Card>

            {isStaffUser(selectedUser) && (
              <UserPermissionsEditor
                userId={selectedUser.id}
                readOnly={userType === 'admin' && isSuperAdminUser(selectedUser)}
                onSaved={async () => {
                  await loadUsers();
                  setSelectedUser(await fetchUserDetail(selectedUser.id));
                }}
              />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowViewModal(false)}>Fermer</Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
